import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { FieldError } from 'react-hook-form';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface PasswordFieldProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: FieldError | string;
  onSubmit?: () => void;
  isRegistration?: boolean;
  label?: string;
  fullWidth?: boolean;
  inputSize?: 'default' | 'xl';
}

function PasswordField({
  error,
  onSubmit,
  isRegistration = false,
  label = 'Password',
  fullWidth,
  ...props
}: PasswordFieldProps): React.ReactElement {
  const [showPassword, setShowPassword] = React.useState(false);

  const handleToggle = (): void => setShowPassword((show) => !show);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className='relative'>
      {label && (
        <Label htmlFor='password' className='mb-2'>
          {label}
        </Label>
      )}
      <Input
        type={showPassword ? 'text' : 'password'}
        autoComplete={isRegistration ? 'new-password' : 'current-password'}
        onKeyDown={handleKeyDown}
        id='password'
        {...props}
        className={`pr-10 ${props.className || ''}`}
        fullWidth={fullWidth}
      />
      <button
        type='button'
        tabIndex={-1}
        className='absolute right-2 top-[22px] h-[36px] text-gray-500'
        onClick={handleToggle}
        aria-label={showPassword ? 'Hide password' : 'Show password'}
      >
        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
      {error && (
        <div className='text-red-500 text-xs mt-1 ml-1'>
          {typeof error === 'string' ? error : error.message}
        </div>
      )}
    </div>
  );
}

export default PasswordField;
